import { useEffect } from 'react';
import { QueryClient, useQuery, useQueryClient, useMutation, UseQueryResult, UseQueryOptions } from 'react-query';
import { omit, sortBy } from 'lodash';
import { updateConsultantTutorialState } from '../../reducers/ConsultantTutorialReducer/ConsultantTutorialReducer';

import { stringifyFilters } from '../../containers/DataExplorer/Filters/queryTransformers';

import {
  AlphaDiversityRangeResponse,
  AlphaPyramidMultipliersResponse,
  CategoryShort,
  CompetitionResponse,
  CompetitionSubmissionsResponse,
  DataCategoriesResponse,
  DataCategory,
  DataSearchResponse,
  DatasetDescriptionResponse,
  DatasetFieldsResponse,
  DatasetsResponse,
  DataStatsResponse,
  fetchAgreement,
  FetchAgreementParams,
  fetchAllDatasetFields,
  fetchAlphaChartRecordSet,
  FetchAlphaChartRecordSetResponse,
  fetchAlphaDistribution,
  fetchAlphaDiversityRange,
  fetchAlphaTutorialChecks,
  fetchAlphaTutorialState,
  fetchCompetition,
  fetchCompetitionSubmissions,
  fetchCompetitionTeamsByPrefix,
  fetchCompetitionUsersByPrefix,
  fetchConsultantPerformance,
  fetchOsmosisSummary,
  fetchOsmosisScaleStatus,
  OsmosisScaleStatusResult,
  OsmosisSummaryResponse,
  fetchDataCategories,
  fetchDataSearch,
  fetchDatasetCoverage,
  FetchDatasetCoverageParams,
  fetchDatasetDescription,
  FetchDatasetDescriptionParams,
  fetchDatasetFields,
  fetchDatasets,
  fetchDatasetTurnover,
  FetchDatasetTurnoverParams,
  fetchDataStats,
  fetchFieldCoverage,
  FetchFieldCoverageParams,
  fetchFieldDescription,
  FetchFieldDescriptionParams,
  fetchFieldDistribution,
  FetchFieldDistributionParams,
  fetchFieldRange,
  FetchFieldRangeParams,
  fetchFieldTurnover,
  FetchFieldTurnoverParams,
  fetchMostPopularDatasets,
  fetchNewestDatasets,
  fetchOperatorDescription,
  fetchOperators,
  fetchPyramidMultipliers,
  fetchReferrals,
  fetchSimulationActivity,
  fetchStreak,
  fetchSubmissionActivity,
  fetchTutorialPageContent,
  fetchTutorialSteps,
  fetchConsultantTutorialStepDetails,
  patchConsultantTutorialStep,
  patchConsultantTutorialActive,
  fetchLearnVideoCourses,
  FieldDescriptionResponse,
  GraphResponse,
  ReferralResponse,
  Region,
  TopDatasetsResponse,
  fetchPowerPoolRegions,
  fetchSpcBoardChoices,
  ConsultantTutorialStepDetailsResponse,
  fetchPromptSubmissions,
  fetchPromptSubmissionOptions,
  fetchPromptWeightHistory,
  postPromptSubmission,
  patchPromptWeight,
  PromptSubmission,
  PromptSubmissionsResponse,
  PromptSubmissionOptions,
  PromptWeightHistoryResponse,
  CreatePromptSubmissionPayload,
} from './api';
import { shouldRetryExceptNotFound } from './utils/shouldRetry';
import { getIsSearchDepthLimitError } from './utils/fetchErrors';
import { useSelector } from 'react-redux';
import { hasConsultantPermission } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { useAppDispatch } from '../services/store/useAppDispatch';

const ONE_HOUR = 60 * 60 * 1000;
export const SEARCH_RESULTS_LIMIT = 100;

export const queryClient = new QueryClient();

export type UseAgreementParams = FetchAgreementParams;

export function useAgreement(params: UseAgreementParams) {
  return useQuery(['agreements', omit(params, 'scope', 'enabled')], async () => fetchAgreement(params), {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    enabled: params.enabled,
  });
}

interface UseReferralsParams {
  order?: string;
  isAscending?: boolean;
}

export function useReferrals({ order = 'dateCreated', isAscending = false }: UseReferralsParams) {
  return useQuery<ReferralResponse, Error>(['referrals', { order, isAscending }], async () =>
    fetchReferrals({
      order,
      isAscending,
    })
  );
}

export function useCompetitionSubmissions(competitionId: string) {
  return useQuery<CompetitionSubmissionsResponse, Error>(
    ['machineAlphaSubmissions', competitionId],
    async () => fetchCompetitionSubmissions(competitionId),
    {
      refetchOnWindowFocus: false,
    }
  );
}

export function useDatasetDescription({ id }: FetchDatasetDescriptionParams) {
  return useQuery<DatasetDescriptionResponse, Error>(
    ['datasetDescription', { id }],
    async () => {
      const result = await fetchDatasetDescription({ id });
      // show delay 1 first
      result.data.sort((first, second) => second.delay - first.delay);
      return result;
    },
    {
      staleTime: ONE_HOUR,
      retry: shouldRetryExceptNotFound,
    }
  );
}

export function useFieldDescription({ id }: FetchFieldDescriptionParams) {
  return useQuery<FieldDescriptionResponse, Error>(
    ['fieldDescription', { id }],
    async () => {
      const result = await fetchFieldDescription({ id });
      // show delay 1 first
      result.data.sort((first, second) => second.delay - first.delay);
      return result;
    },
    {
      staleTime: ONE_HOUR,
      retry: shouldRetryExceptNotFound,
    }
  );
}

export function useFieldCoverage({ id, region, delay }: FetchFieldCoverageParams) {
  return useQuery<GraphResponse, Error>(
    ['fieldCoverage', { id, region }],
    async () => fetchFieldCoverage({ id: id as string, region, delay }),
    { enabled: region != null && id != null, staleTime: ONE_HOUR }
  );
}

export function useFieldTurnover({ id, region, delay }: FetchFieldTurnoverParams) {
  return useQuery<GraphResponse, Error>(
    ['fieldTurnover', { id, region }],
    async () => fetchFieldTurnover({ id: id as string, region, delay }),
    { enabled: region != null && id != null, staleTime: ONE_HOUR }
  );
}

export function useFieldRange({ id, region, delay }: FetchFieldRangeParams) {
  return useQuery<GraphResponse, Error>(
    ['fieldRange', { id, region }],
    async () =>
      fetchFieldRange({
        id: id as string,
        region,
        delay,
      }),
    {
      enabled: region != null && id != null,
      staleTime: ONE_HOUR,
    }
  );
}

export function useFieldDistribution({ id, region }: Partial<FetchFieldDistributionParams>) {
  return useQuery<GraphResponse, Error>(
    ['fieldDistribution', { id, region }],
    async () => fetchFieldDistribution({ id: id as string, region: region as string }),
    { enabled: region != null && id != null, staleTime: ONE_HOUR }
  );
}

export function useDatasetCoverage({ id, region, delay }: FetchDatasetCoverageParams) {
  return useQuery<GraphResponse, Error>(
    ['datasetCoverage', { id, region, delay }],
    async () => fetchDatasetCoverage({ id: id as string, region: region as string, delay }),
    { enabled: region != null && id != null, staleTime: ONE_HOUR }
  );
}

export function useDatasetTurnover({ id, region, delay }: FetchDatasetTurnoverParams) {
  return useQuery<GraphResponse, Error>(
    ['datasetTurnover', { id, region }],
    async () => fetchDatasetTurnover({ id: id as string, region: region as string, delay }),
    { enabled: region != null && id != null, staleTime: ONE_HOUR }
  );
}

export type EnhancedQueryResult<TData, TError> = UseQueryResult<TData, TError> & {
  isSearchLimitExceeded: boolean;
};

export function useDatasets({ queryObject }: { queryObject: Record<string, any> }): EnhancedQueryResult<DatasetsResponse, Error> {
  const query = stringifyFilters(queryObject);
  const isSearchActive = !!queryObject.search?.trim();
  const offset = Number(queryObject.offset || 0);

  // Check if we're trying to paginate past the 100 result limit during search
  const isSearchLimitExceeded = isSearchActive && offset >= SEARCH_RESULTS_LIMIT;

  const result = useQuery<DatasetsResponse, Error>(
    ['datasets', { query }],
    async () => {
      return fetchDatasets({ query });
    },
    {
      keepPreviousData: true,
      retry: (failureCount, error) => {
        // Don't retry on search depth limit error
        if (getIsSearchDepthLimitError(error)) return false;
        return failureCount < 1;
      },
      staleTime: ONE_HOUR,
      enabled: !isSearchLimitExceeded, // Don't fetch if search limit would be exceeded
    }
  );

  usePrefetchNextFilterPage({ result, queryObject, key: 'datasets', fetchFunction: fetchDatasets, isSearchActive });

  // Add a flag to indicate if search limit is exceeded
  return {
    ...result,
    isSearchLimitExceeded: isSearchLimitExceeded || getIsSearchDepthLimitError(result.error),
  };
}

export function useDatasetFields({ queryObject }: { queryObject: Record<string, any> }) {
  const query = stringifyFilters(queryObject);

  const result = useQuery<DatasetFieldsResponse, Error>(
    ['dataset-fields', { query }],
    async () => {
      return fetchDatasetFields({ query });
    },
    { staleTime: ONE_HOUR, keepPreviousData: true }
  );

  usePrefetchNextFilterPage({ result, queryObject, key: 'dataset-fields', fetchFunction: fetchDatasetFields });

  return result;
}

export function addDatasetGroups(result: DatasetFieldsResponse) {
  for (let i = 0; i < result.results.length; i++) {
    let groupIndex = 0;
    let prevName = '';
    for (let i = 0; i < result.results.length; i++) {
      const currentName = result.results[i].dataset.name ?? '';
      if (currentName !== prevName) {
        groupIndex++;
        prevName = currentName;
      }
      result.results[i].dataset!.group = groupIndex;
    }
  }
}

export function useAllDatasetFields({ queryObject }: { queryObject: any }): EnhancedQueryResult<DatasetFieldsResponse, Error> {
  const query = stringifyFilters(queryObject);
  const isSearchActive = !!queryObject.search?.trim();
  const offset = Number(queryObject.offset || 0);

  const isSearchLimitExceeded = isSearchActive && offset >= SEARCH_RESULTS_LIMIT;

  const result = useQuery<DatasetFieldsResponse, Error>(
    ['all-dataset-fields', { query }],
    async () => {
      const result = await fetchAllDatasetFields({ query });
      addDatasetGroups(result);

      return result;
    },
    {
      staleTime: ONE_HOUR,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (getIsSearchDepthLimitError(error)) return false;
        return failureCount < 1;
      },
      enabled: !isSearchLimitExceeded,
    }
  );

  usePrefetchNextFilterPage({ result, queryObject, key: 'dataset-fields', fetchFunction: fetchDatasetFields, isSearchActive });

  return {
    ...result,
    isSearchLimitExceeded: isSearchLimitExceeded || getIsSearchDepthLimitError(result.error),
  };
}

export function useDataCategories() {
  return useQuery<DataCategoriesResponse, Error>(
    ['data-categories'],
    async () => {
      return fetchDataCategories();
    },
    { staleTime: ONE_HOUR, keepPreviousData: true }
  );
}

const usePrefetchNextFilterPage = ({
  result,
  queryObject,
  key,
  fetchFunction,
  isSearchActive = false,
}: {
  result: UseQueryResult<any>;
  queryObject: any;
  key: string;
  fetchFunction: ({ query }: { query: string }) => Promise<any>;
  isSearchActive?: boolean;
}) => {
  const queryClient = useQueryClient();

  const limit = Number(queryObject?.limit);
  const offset = Number(queryObject?.offset);

  useEffect(() => {
    // Don't prefetch if search is active and we're at or past the limit
    if (isSearchActive && offset + limit >= SEARCH_RESULTS_LIMIT) {
      return;
    }

    if (result.data?.next) {
      const newQueryObject = { ...queryObject, offset: offset + limit };
      const newQueryString = stringifyFilters(newQueryObject);

      queryClient.prefetchQuery([key, { query: newQueryString }], () => fetchFunction({ query: newQueryString }), {
        retry: 1,
        staleTime: ONE_HOUR,
      });
    }
  }, [result.data?.next, limit, offset, queryClient, isSearchActive]);
};

export function useDataStats() {
  return useQuery<DataStatsResponse, Error>(['dataStats'], async () => fetchDataStats(), { staleTime: ONE_HOUR });
}

export function useMostPopularDatasets() {
  return useQuery<TopDatasetsResponse, Error>(['popularDatasets'], async () => fetchMostPopularDatasets(), { staleTime: ONE_HOUR });
}

export interface CommonDataFields {
  region: Region;
  delay: string;
  universe: string;
  instrumentType: string;
}

export function useThemeDatasets({ region, delay, universe }: CommonDataFields) {
  const queryObject = {
    region,
    delay,
    universe,
    instrumentType: 'EQUITY',
    limit: '4',
    theme: 'true',
  };
  return useDatasets({ queryObject });
}

export function useNewestDatasets() {
  return useQuery<TopDatasetsResponse, Error>(['newestDatasets'], async () => fetchNewestDatasets(), { staleTime: ONE_HOUR });
}

export function useDataSearch({ queryObject, enabled = true }: { queryObject: any; enabled?: boolean }) {
  const query = stringifyFilters(queryObject);
  return useQuery<DataSearchResponse, Error>(['dataSearch', { query }], async () => fetchDataSearch({ query }), {
    staleTime: ONE_HOUR,
    enabled,
    keepPreviousData: false,
  });
}

export function useCompetition(id: string) {
  return useQuery<CompetitionResponse, Error>(['competition', { id }], async () => fetchCompetition({ id }), { retry: false });
}

export function useAlphaDistribution(hierarchy: string[]) {
  return useQuery(['alphaDistribution', ...hierarchy], async () => fetchAlphaDistribution(hierarchy), {
    enabled: hierarchy?.length > 0,
    retry: 1,
  });
}

interface UseAlphaPyramidParams {
  startDate?: string;
  endDate?: string;
}

export interface DelayByRegion {
  region: string;
  delay: number[];
}

export interface AlphaPyramid {
  category: Pick<DataCategory, 'id' | 'name'>;
  regions: {
    [key in string]?: { alphaCount: number };
  };
}

export interface PyramidMultiplier {
  category: Pick<DataCategory, 'id' | 'name'>;
  regions: {
    [key in string]?: { pyramidMultiplier: number };
  };
}

function getAlphaPyramidData(data: AlphaDiversityRangeResponse) {
  const getGroupKey = (categoryId: string, region: string, delay: number) => [categoryId, region, delay].join('_');

  const validGroupMap = new Map();
  const categories: CategoryShort[] = [];
  const visitedCategories = new Set();
  const regions: Record<string, Set<number>> = {};

  data.pyramids.forEach(pyramid => {
    validGroupMap.set(getGroupKey(pyramid.category.id, pyramid.region, pyramid.delay), pyramid.alphaCount);
    if (!visitedCategories.has(pyramid.category.id)) {
      visitedCategories.add(pyramid.category.id);
      categories.push(pyramid.category);
    }
    if (!regions[pyramid.region]) {
      regions[pyramid.region] = new Set();
    }
    const regionDelays = regions[pyramid.region];
    regionDelays.add(pyramid.delay);
  });

  const sortedCategories = sortBy(categories, 'name');

  const regionNames = Object.keys(regions);
  return {
    data: sortedCategories.map(category => ({
      category,
      regions: regionNames.reduce<AlphaPyramid['regions']>((prev, region) => {
        for (const delay of regions[region]) {
          const groupKey = getGroupKey(category.id, region, delay);
          const isValidGroup = validGroupMap.has(groupKey);
          if (isValidGroup) {
            const key = [region, delay].join('_');
            const alphaCount = validGroupMap.get(groupKey) ?? 0;
            prev[key] = { alphaCount };
          }
        }
        return prev;
      }, {}),
    })),
    regions: regionNames.map(region => ({ region, delay: Array.from(regions[region]) })),
  };
}

export function useAlphaPyramid({ startDate, endDate }: UseAlphaPyramidParams) {
  return useQuery(
    ['alphaDiversityRange', { startDate, endDate }],
    async () =>
      fetchAlphaDiversityRange({
        startDate,
        endDate,
      }),
    {
      select: data => getAlphaPyramidData(data),
    }
  );
}

function getPyramidMultiplierData(data: AlphaPyramidMultipliersResponse) {
  const getGroupKey = (categoryId: string, region: string, delay: number) => [categoryId, region, delay].join('_');

  const validGroupMap = new Map();
  const categories: CategoryShort[] = [];
  const visitedCategories = new Set();
  const regions: Record<string, Set<number>> = {};

  data.pyramids.forEach(pyramid => {
    validGroupMap.set(getGroupKey(pyramid.category.id, pyramid.region, pyramid.delay), pyramid.multiplier);
    if (!visitedCategories.has(pyramid.category.id)) {
      visitedCategories.add(pyramid.category.id);
      categories.push(pyramid.category);
    }
    if (!regions[pyramid.region]) {
      regions[pyramid.region] = new Set();
    }
    const regionDelays = regions[pyramid.region];
    regionDelays.add(pyramid.delay);
  });

  const sortedCategories = sortBy(categories, 'name');

  const regionNames = Object.keys(regions);
  return {
    data: sortedCategories.map(category => ({
      category,
      regions: regionNames.reduce<PyramidMultiplier['regions']>((prev, region) => {
        for (const delay of regions[region]) {
          const groupKey = getGroupKey(category.id, region, delay);
          const isValidGroup = validGroupMap.has(groupKey);
          if (isValidGroup) {
            const key = [region, delay].join('_');
            const pyramidMultiplier = validGroupMap.get(groupKey) ?? 0;
            prev[key] = { pyramidMultiplier };
          }
        }
        return prev;
      }, {}),
    })),
    regions: regionNames.map(region => ({ region, delay: Array.from(regions[region]) })),
  };
}

export function useAlphaPyramidMultipliers() {
  return useQuery(['pyramidMultipliers'], async () => fetchPyramidMultipliers(), {
    select: data => getPyramidMultiplierData(data),
  });
}

interface UseAlphaChartRecordSetParams {
  alphaId: string;
  chartType: string;
  isSimulation: boolean;
}

export function useAlphaChartRecordSet({ alphaId, chartType, isSimulation }: UseAlphaChartRecordSetParams) {
  // In simulation, when switch tab, alpha chart should be not reloaded (leave cache behaviour as default)
  // But in alpha lists, when switch alpha, alpha chart can be reloaded (disable cache)
  const cacheTime = isSimulation ? undefined : 0;

  return useQuery<FetchAlphaChartRecordSetResponse, Error>(
    ['alpha', alphaId, 'recordset', chartType],
    async () => fetchAlphaChartRecordSet({ alphaId, chartType }),
    { staleTime: ONE_HOUR, cacheTime }
  );
}

export function useStreaks({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: ['streak'], queryFn: fetchStreak, staleTime: ONE_HOUR, enabled });
}

export function useSimulationActivity(startDate?: string) {
  return useQuery(['activity', 'simulation', startDate], async () => fetchSimulationActivity(startDate), {
    staleTime: ONE_HOUR,
    enabled: !!startDate,
    retry: 1,
  });
}

export function useSubmissionActivity(startDate?: string) {
  return useQuery(['activity', 'submission', startDate], async () => fetchSubmissionActivity(startDate), {
    staleTime: ONE_HOUR,
    enabled: !!startDate,
    retry: 1,
    // This API is not optimized so this helps reducing stress to API
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
}

interface UseCompetitionTeamsByPrefixParams {
  competitionId: string;
  prefix: string;
  type: 'team' | 'user';
  boardType?: string;
  minPrefixLength: number;
  boardRegion?: string;
}

export function useCompetitionTeamsByPrefix({
  competitionId,
  prefix,
  type,
  boardType,
  minPrefixLength,
  boardRegion,
}: UseCompetitionTeamsByPrefixParams) {
  const key = type === 'team' ? 'competitionTeamsByPrefix' : 'competitionUsersByPrefix';
  const fetchMethod = type === 'team' ? fetchCompetitionTeamsByPrefix : fetchCompetitionUsersByPrefix;
  // boardType/boardRegion are request inputs for consultant boards, so include
  // them in the cache key to avoid serving stale suggestions across boards or
  // periods. Including them unconditionally is simpler and strictly correct —
  // undefined entries key fine in react-query, and non-consultant boards pay
  // only a one-time cache miss after deploy.
  const queryKey = [key, competitionId, prefix, boardType, boardRegion];
  return useQuery(
    queryKey,
    async () =>
      fetchMethod({
        competitionId,
        prefix,
        boardType,
        boardRegion,
      }),
    {
      staleTime: ONE_HOUR,
      enabled: prefix?.length >= minPrefixLength,
    }
  );
}

export interface UseConsultantPerformanceParams {
  userId?: string;
  isDisabled?: boolean;
}

export const CONSULTANT_SUMMARY_QUERY_KEY = ['users', 'self', 'consultant', 'summary'];

export function useConsultantPerformance({ userId = 'self', isDisabled = false }: UseConsultantPerformanceParams = {}) {
  const isConsultant = useSelector(hasConsultantPermission);

  return useQuery(CONSULTANT_SUMMARY_QUERY_KEY, async () => fetchConsultantPerformance({ userId }), {
    enabled: isConsultant && !isDisabled,
    staleTime: ONE_HOUR,
  });
}

export const OSMOSIS_SUMMARY_QUERY_KEY = ['users', 'self', 'osmosis', 'summary'];

export function useOsmosisSummary() {
  const isConsultant = useSelector(hasConsultantPermission);

  return useQuery<OsmosisSummaryResponse>({
    queryKey: OSMOSIS_SUMMARY_QUERY_KEY,
    queryFn: async () => fetchOsmosisSummary(),
    enabled: isConsultant,
    staleTime: ONE_HOUR,
  });
}

export const OSMOSIS_SCALE_STATUS_QUERY_KEY = ['users', 'self', 'osmosis', 'scale-points', 'status'];

export function useOsmosisScaleStatusQuery(options?: UseQueryOptions<OsmosisScaleStatusResult>) {
  return useQuery<OsmosisScaleStatusResult>(OSMOSIS_SCALE_STATUS_QUERY_KEY, async () => fetchOsmosisScaleStatus(), options);
}

export function useOperators() {
  return useQuery('operators', fetchOperators, {
    staleTime: ONE_HOUR,
  });
}

export function useOperatorDescription(operatorPath: string) {
  return useQuery(['operatorDescription', operatorPath], async () => fetchOperatorDescription(operatorPath), {
    staleTime: ONE_HOUR,
  });
}

interface UseAlphaTutorialChecksParams {
  alphaId: string;
  isSubmitted: boolean;
  isDisabled?: boolean;
}

export function useAlphaTutorialChecks({ alphaId, isSubmitted, isDisabled = false }: UseAlphaTutorialChecksParams) {
  return useQuery(['tutorialChecks', alphaId, isSubmitted], async () => fetchAlphaTutorialChecks(alphaId), {
    staleTime: ONE_HOUR,
    enabled: !isDisabled,
  });
}

export function useTutorialPageContent(tutorialPage?: string) {
  return useQuery(['tutorialPage', tutorialPage], async () => fetchTutorialPageContent(tutorialPage as string), {
    staleTime: ONE_HOUR,
    enabled: typeof tutorialPage === 'string',
  });
}

export function useTutorialSteps() {
  return useQuery('tutorialSteps', async () => fetchTutorialSteps(), {
    staleTime: ONE_HOUR,
  });
}

export function useTutorialState() {
  return useQuery('tutorialState', async () => fetchAlphaTutorialState(), {
    staleTime: ONE_HOUR,
  });
}

/**
 * Hook for updating the current step in the consultant tutorial with optimistic updates
 * @returns A mutation function to update the current step
 */
export function usePatchConsultantTutorialStep() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (newStep: number) => patchConsultantTutorialStep(newStep),
    // When mutate is called:
    onMutate: async newStep => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['consultantTutorialStepDetails'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ConsultantTutorialStepDetailsResponse>('consultantTutorialStepDetails');

      // Optimistically update to the new value
      if (previousData) {
        const updatedData = {
          ...previousData,
          currentStep: newStep,
        };

        // Update React Query cache
        queryClient.setQueryData<ConsultantTutorialStepDetailsResponse>('consultantTutorialStepDetails', updatedData);

        // Update Redux state
        dispatch(updateConsultantTutorialState(updatedData));
      }

      // Return a context object with the snapshotted value
      return { previousData };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, newStep, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData('consultantTutorialStepDetails', context.previousData);
        dispatch(updateConsultantTutorialState(context.previousData));
      }
    },
    // Always refetch after error or success:
    onSettled: data => {
      queryClient.invalidateQueries({ queryKey: ['consultantTutorialStepDetails'] });
    },
  });
}

/**
 * Hook for updating the active state of the consultant tutorial with optimistic updates
 * @returns A mutation function to update the active state
 */
export function usePatchConsultantTutorialActive() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (isActive: boolean) => patchConsultantTutorialActive(isActive),
    // When mutate is called:
    onMutate: async isActive => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['consultantTutorialStepDetails'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ConsultantTutorialStepDetailsResponse>('consultantTutorialStepDetails');

      // Optimistically update to the new value
      if (previousData) {
        const updatedData = {
          ...previousData,
          active: isActive,
        };

        // Update React Query cache
        queryClient.setQueryData<ConsultantTutorialStepDetailsResponse>('consultantTutorialStepDetails', updatedData);

        // Update Redux state
        dispatch(updateConsultantTutorialState(updatedData));
      }

      // Return a context object with the snapshotted value
      return { previousData };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, isActive, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData('consultantTutorialStepDetails', context.previousData);
        dispatch(updateConsultantTutorialState(context.previousData));
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['consultantTutorialStepDetails'] });
    },
  });
}

export function useConsultantTutorialStepDetails() {
  const dispatch = useAppDispatch();

  return useQuery<ConsultantTutorialStepDetailsResponse, Error>(
    'consultantTutorialStepDetails',
    async () => fetchConsultantTutorialStepDetails(),
    {
      staleTime: ONE_HOUR,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        // Update Redux state when data is fetched
        dispatch(updateConsultantTutorialState(data));
      },
    }
  );
}

const LEARN2QUANT_COURSE_IDS = new Set([
  'introduction-quantitative-finance',
  'alpha-examples-data-category',
  'alpha-examples-idea-type-and-delay',
  'combining-alphas-and-risk-management',
  'implementing-advanced-ideas-brain',
]);

export function useLearn2QuantVideoCourses() {
  return useQuery('learn2QuantVideoCourses', async () => fetchLearnVideoCourses(), {
    staleTime: ONE_HOUR,
    select: data => data.results.filter(course => LEARN2QUANT_COURSE_IDS.has(course.id)),
  });
}

export function usePowerPoolRegions() {
  return useQuery('powerPoolRegions', async () => fetchPowerPoolRegions(), {
    staleTime: ONE_HOUR,
  });
}

export function useSpcBoardChoices() {
  return useQuery('spcBoardChoices', async () => fetchSpcBoardChoices(), {
    staleTime: ONE_HOUR,
  });
}

const PROMPT_SUBMISSIONS_QUERY_KEY = 'promptSubmissions';
const PROMPT_SUBMISSION_OPTIONS_QUERY_KEY = 'promptSubmissionOptions';
const PROMPT_WEIGHT_HISTORY_QUERY_KEY = 'promptWeightHistory';

export function usePromptSubmissions(limit: number, offset: number) {
  return useQuery<PromptSubmissionsResponse, Error>(
    [PROMPT_SUBMISSIONS_QUERY_KEY, { limit, offset }],
    async () => fetchPromptSubmissions(limit, offset),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
}

export function usePromptSubmissionOptions() {
  return useQuery<PromptSubmissionOptions, Error>(PROMPT_SUBMISSION_OPTIONS_QUERY_KEY, async () => fetchPromptSubmissionOptions(), {
    staleTime: ONE_HOUR,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function usePromptWeightHistory(id: string, limit: number, offset: number, enabled: boolean) {
  return useQuery<PromptWeightHistoryResponse, Error>(
    [PROMPT_WEIGHT_HISTORY_QUERY_KEY, id, { limit, offset }],
    async () => fetchPromptWeightHistory(id, limit, offset),
    {
      enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
}

export function useCreatePromptSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePromptSubmissionPayload) => postPromptSubmission(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPT_SUBMISSIONS_QUERY_KEY] });
    },
  });
}

export function useUpdatePromptWeight() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, weight }: { id: string; weight: number }) => patchPromptWeight(id, weight),
    onMutate: async ({ id, weight }) => {
      await queryClient.cancelQueries({ queryKey: [PROMPT_SUBMISSIONS_QUERY_KEY] });
      const previousQueries = queryClient.getQueriesData<PromptSubmissionsResponse>([PROMPT_SUBMISSIONS_QUERY_KEY]);
      previousQueries.forEach(([key, data]) => {
        if (!data) return;
        queryClient.setQueryData<PromptSubmissionsResponse>(key, {
          ...data,
          results: data.results.map((submission: PromptSubmission) => (submission.id === id ? { ...submission, weight } : submission)),
        });
      });
      return { previousQueries };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([key, data]: [any, PromptSubmissionsResponse]) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROMPT_SUBMISSIONS_QUERY_KEY] });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: [PROMPT_WEIGHT_HISTORY_QUERY_KEY, variables.id] });
      }
    },
  });
}
